import { PRE_SELECTED_COUNTRIES_PER_LANGUAGE } from 'constants/languages';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router';
import { I18n } from 'utils/i18n';
import usePasskeyAuth from 'utils/usePasskeyAuth';
import * as Yup from 'yup';
import prefixFormat from './prefixFormat';

const useLoginForm = props => {
  const navigate = useNavigate();
  const { checkPasskeyAvailability } = usePasskeyAuth();

  const formik = useFormik({
    initialValues: {
      prefix: props.login?.form?.prefix || PRE_SELECTED_COUNTRIES_PER_LANGUAGE[props.locale],
      phone: props.login?.form?.phone || '',
      keepSessionOpen: props.keepSessionOpen ? ['on'] : [],
      returnUrl: props.returnUrl || '',
    },
    validationSchema: Yup.object().shape({
      prefix: Yup.string().required('Please choose your country prefix'),
      phone: Yup.number().required('Please enter your number'),
    }),
    validate: values => {
      const errors = {};
      const { length } = prefixFormat(values.prefix).match(/#/g) || [];
      if (values.phone && values.phone.replace(/\D/g, '').length !== length) {
        errors.phone = I18n.t('AuthErrors.PhoneFormatError');
      }

      if (values.prefix === 'Choose country prefix') {
        errors.prefix = 'Please choose your country prefix';
      }

      return errors;
    },
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        const { keepSessionOpen, prefix, phone, returnUrl } = values;
        const formattedPhone = phone.replace(/\D/g, '');
        const inviteToken = returnUrl && returnUrl.match(/\/accept-invite\/([a-z0-9]+)$/)?.[1];

        // Update form state
        props.actions.authConfig.setKeepSessionOpen(keepSessionOpen[0] === 'on');
        await props.actions.login.form.update({ prefix, phone });

        // Check for passkey support and availability
        if (checkPasskeyAvailability && typeof checkPasskeyAvailability === 'function') {
          const availability = await checkPasskeyAvailability(formattedPhone, prefix);
          const hasPasskey = availability === true || availability?.hasPasskey === true;
          const loginType = availability?.loginType;

          if (hasPasskey) {
            // Navigate to passkey selection screen instead of auto-triggering
            // This gives password managers like LastPass time to initialize
            // and ensures the passkey flow is user-initiated
            setSubmitting(false);
            navigate('/auth/login/passkey-cancelled', {
              state: {
                phoneNumber: formattedPhone,
                countryCode: prefix,
                keepSessionOpen: keepSessionOpen[0] === 'on',
                inviteToken,
                locale: props.locale,
                isInitialPrompt: true, // Flag to show different messaging
                ...(loginType === 'password' ? { passwordLogin: true } : {}),
              },
              replace: true,
            });
            return;
          }
        }

        // Fall back to SMS if no passkey or passkey check failed
        await props.actions.login.sendSMS(`${prefix}-${phone}`, {
          keepSessionOpen: keepSessionOpen[0] === 'on',
          inviteToken,
          locale: props.locale,
        });
        setSubmitting(false);
        navigate('confirmation');
      } catch (e) {
        if (e.data?.data?.status === 404) {
          navigate('../login-no-invitation');
          return;
        }
        setSubmitting(false);
        const errCode = e.data?.error?.key || e.data?.key || 'UnexpectedError';
        setErrors({ phone: I18n.t(`AuthErrors.${errCode}`) });
      }
    },
  });

  return formik;
};

export default useLoginForm;
